// Dock unique + sur-dock (décision Vincent 21/10, comparateur acs-sur-dock, variante V3 polie). Le même dock à 5 entrées sous
// toutes les pages ACS : Accueil et les quatre espaces ; l'actif en pilule encre avec son nom, un point or sur un espace où
// quelque chose t'attend. Tap sur un autre espace = y aller (dernière rubrique ouverte) ; tap sur l'espace actif, ou appui long
// sur n'importe lequel = la feuille courte de ses rubriques, chacune avec ce qui t'y attend (données du store et des fixtures).
// Clavier : flèches haut/bas dans la feuille, Échap ferme et rend le focus à l'espace. Mouvement réduit : fondu seul.
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ChevronRight, House } from "lucide-react";
import { DOMAINS, domainOf, locate, rubHref, useHash, type DomainId } from "./domains";
import { AppIcon, AppIconSprite } from "./app-icons";
import { useStore } from "./store";
import { useMessagerie, COACHES } from "./messagerie-state";
import { DEMO_TIME, DEMO_TODAY } from "./demo-context";
import { TODAY_KEY } from "./home-fixtures";
import { MEAL_LABEL, PLAN, RECIPES, type MealId } from "./nutrition-fixtures";
import { BILANS, SESSIONS } from "./suivi-fixtures";
import { CATALOGUE } from "./banque-fixtures";
import { STUDIO } from "./home-fixtures";
import "./dock.css";

type Status = { text: string; go?: boolean };
type Entry = { id: "accueil" | DomainId; label: string; icon: ReactNode };
const ENTRIES: Entry[] = [{ id: "accueil", label: "Accueil", icon: <House size={20} strokeWidth={1.8} /> },
  ...DOMAINS.map(d => ({ id: d.id, label: d.label, icon: d.icon }))];
const MEAL_HOUR: Record<MealId, string> = { "petit-dejeuner": "08:00", dejeuner: "12:30", collation: "16:30", diner: "19:30" };
const SUB: Record<DomainId, string> = { entrainement: "Semaine du 12 octobre", nutrition: "Lundi 12 octobre", suivi: "Lundi 12 octobre", studio: "ACS Studio" };
const dayLong = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" }).replace(".", "");

/** Ce qui attend dans chaque rubrique, en une ligne. `go` = une action du jour (ligne en or, point sur le dock). */
function useStatuses(): Record<DomainId, Record<string, Status>> {
  const s = useStore();
  const msgs = useMessagerie();
  const booked = s.slots.filter(x => x.state === "booked" && (x.day > TODAY_KEY || (x.day === TODAY_KEY && x.time > DEMO_TIME))).sort((a, b) => (a.day + a.time).localeCompare(b.day + b.time))[0];
  const plan = PLAN?.[TODAY_KEY] ?? {};
  const nextMeal = (Object.keys(plan) as MealId[]).filter(m => MEAL_HOUR[m] > DEMO_TIME).sort((a, b) => MEAL_HOUR[a].localeCompare(MEAL_HOUR[b]))[0];
  const coach = COACHES.find(c => (msgs.unread[c.id] ?? 0) > 0);
  const ck = s.checkins[DEMO_TODAY];
  const bilan = BILANS.find(b => !b.done);
  const sessions = SESSIONS.length + s.doneSessions.filter(x => !SESSIONS.some(y => y.id === x.id)).length;
  return {
    entrainement: {
      semaine: booked ? { text: `${booked.title.replace(/^Small group · /, "")} ${booked.day === TODAY_KEY ? "ce soir" : ""} · ${booked.time}`.replace("  ", " "), go: booked.day === TODAY_KEY } : { text: "Réserver un cours" },
      historique: { text: `${sessions} séances réalisées` },
      exercices: { text: `${CATALOGUE.length} fiches, vidéos et protocoles` },
    },
    nutrition: {
      repas: nextMeal ? { text: `${MEAL_LABEL[nextMeal]} à ${MEAL_HOUR[nextMeal]}`, go: true } : { text: "Mes repas de la semaine" },
      recettes: { text: `${RECIPES.length} recettes` },
      courses: { text: "La liste de la semaine" },
      journal: { text: "Photos et ressentis du jour" },
    },
    suivi: {
      aujourdhui: coach ? { text: `${coach.name.split(" ")[0]} t'a écrit`, go: true } : ck ? { text: "Check-in envoyé" } : { text: "Check-in à faire", go: true },
      tendances: { text: "Sommeil, stress, poids, régularité" },
      bilans: bilan ? { text: `${bilan.title} · ${dayLong(bilan.date)}` } : { text: "Mes bilans" },
    },
    studio: {
      actus: STUDIO.news ? { text: STUDIO.news.title } : { text: "Les nouvelles du studio" },
      infos: { text: "Horaires, accès, contact" },
    },
  };
}

export function GlobalDock({ hidden }: { hidden?: boolean }) {
  const hash = useHash();
  const loc = locate(hash);
  const active: Entry["id"] = loc?.domain ?? "accueil";
  const st = useStatuses();
  const [open, setOpen] = useState<DomainId | null>(null);
  const [mode, setMode] = useState<"pointer" | "keyboard">("pointer");
  const sheet = useRef<HTMLDivElement>(null);
  const dock = useRef<HTMLElement>(null);
  const press = useRef<{ t?: number; long: boolean }>({ long: false });

  useEffect(() => { setOpen(null); }, [hash]);
  useEffect(() => { if (hidden) setOpen(null); }, [hidden]);
  // Échap ferme même quand le focus est resté sur la page (ouverture au doigt).
  useEffect(() => {
    if (!open) return;
    const k = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); setOpen(null); dock.current?.querySelector<HTMLButtonElement>(`[data-id="${open}"]`)?.focus(); } };
    window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k);
  }, [open]);
  useEffect(() => { if (open) requestAnimationFrame(() => sheet.current?.querySelector<HTMLButtonElement>(mode === "keyboard" ? "button" : "button[data-cur]")?.focus({ preventScroll: true })); }, [open, mode]);

  const close = (refocus = false) => { const was = open; setOpen(null); if (refocus && was) dock.current?.querySelector<HTMLButtonElement>(`[data-id="${was}"]`)?.focus(); };
  const tap = (id: Entry["id"]) => {
    if (press.current.long) { press.current.long = false; return; }
    if (open) { const was = open; setOpen(null); if (was === id) return; }
    if (id === "accueil") { window.location.hash = "#/accueil"; return; }
    if (id === active) { setOpen(id); return; }
    window.location.hash = rubHref(id);
  };
  const down = (id: Entry["id"]) => { if (id === "accueil") return; press.current.long = false; press.current.t = window.setTimeout(() => { press.current.long = true; navigator.vibrate?.(8); setOpen(id); }, 450); };
  const up = () => { window.clearTimeout(press.current.t); };
  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const btns = [...(sheet.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const i = btns.indexOf(document.activeElement as HTMLButtonElement);
    e.preventDefault(); btns[(i + (e.key === "ArrowDown" ? 1 : -1) + btns.length) % btns.length]?.focus();
  };

  const dom = open ? domainOf(open) : null;
  const dots = (id: Entry["id"]) => id !== "accueil" && Object.values(st[id]).some(x => x.go);

  return <div className="acs-dock-root" data-hidden={hidden || undefined} inert={hidden || undefined} aria-hidden={hidden || undefined}
    onKeyDownCapture={e => { if (e.key === "Tab" || e.key.startsWith("Arrow")) setMode("keyboard"); }} onPointerDownCapture={() => setMode("pointer")}>
    <AppIconSprite />
    <div className="acs-dock-scrim" data-open={open ? "" : undefined} onClick={() => close()} aria-hidden="true" />
    {dom && <div ref={sheet} className="acs-surdock" role="dialog" aria-label={`Rubriques ${dom.label}`} onKeyDown={keys}>
      <div className="acs-surdock-h">
        <span className="acs-surdock-ic"><AppIcon domain={dom.id} size={40} /></span>
        <span><b>{dom.label}</b><small>{SUB[dom.id]}</small></span>
      </div>
      <ul>{dom.rubriques.map((r, i) => {
        const cur = loc?.domain === dom.id && loc.rub === r.id, s = st[dom.id][r.id];
        return <li key={r.id} style={{ animationDelay: `${60 + i * 40}ms` }}>
          <button type="button" data-cur={cur || undefined} aria-current={cur ? "page" : undefined}
            onClick={() => { setOpen(null); window.location.hash = rubHref(dom.id, r.id); }}>
            <span className="acs-surdock-ri">{r.icon}</span>
            <span className="acs-surdock-t"><b>{r.label}</b>{s && <small data-go={s.go || undefined}>{s.text}</small>}</span>
            {cur ? <span className="acs-surdock-here">Ici</span> : <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />}
          </button>
        </li>;
      })}</ul>
    </div>}
    <nav ref={dock} className="acs-dock" aria-label="Espaces" data-input={mode}>
      {ENTRIES.map(e => {
        const on = e.id === active;
        return <button key={e.id} type="button" data-id={e.id} className="acs-dk" data-on={on || undefined} data-peek={open === e.id && !on || undefined}
          aria-current={on ? "page" : undefined} aria-label={e.label} aria-haspopup={e.id === "accueil" ? undefined : "dialog"} aria-expanded={e.id === "accueil" ? undefined : open === e.id}
          onPointerDown={() => down(e.id)} onPointerUp={up} onPointerLeave={up} onPointerCancel={up} onContextMenu={ev => ev.preventDefault()} onClick={() => tap(e.id)}>
          {e.icon}<span>{e.label}</span>{dots(e.id) && !on && <i className="acs-dk-dot" aria-hidden="true" />}
        </button>;
      })}
    </nav>
  </div>;
}
